import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import '../../../node_modules/bootstrap/dist/css/bootstrap.min.css';
import TableContainer from '../../components/Common/TableContainer';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';

//import components
import Breadcrumbs from '../../components/Common/Breadcrumb';
import DeleteModal from '../../components/Common/DeleteModal';
//Import Flatepicker
import 'flatpickr/dist/themes/material_blue.css';

import {
  addNewMember as onAddNewMember,
  updateMember as onUpdateMember,
  deleteMember as onDeleteMember,
  setMemberSummary,
} from 'store/actions';

import {
  MemberId,
  PrimaryText,
  BillingName,
  Date,
  PaymentStatus,
  PolicyStatus,
} from './MemberCol';

//redux
import { useSelector, useDispatch } from 'react-redux';
import MembersModal from './MemberModal';
import { useMutation, useQueryClient } from 'react-query';

import {
  Col,
  Row,
  Modal,
  ModalHeader,
  ModalBody,
  Form,
  Input,
  FormFeedback,
  Label,
  Card,
  CardBody,
} from 'reactstrap';
import moment from 'moment';
import { ToastContainer } from 'react-toastify';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAllAllMembersApi } from 'api/member';
import { useRole } from 'hooks/useRole';
import { useAuth } from 'hooks/useAuth';
import { getSearchColumns, getUserAddress, constructAddress, sumArray } from 'utils/tools';
import { getMemberStatusCountsApi } from 'api/report';
import MainFilter from 'components/Common/MainFilter';
import { allowedEmails } from 'configs/constants';
import Spinners from 'components/Common/Spinner';
import StatCards from 'components/Common/StatCards';
import { formatDate } from 'utils/dates';

function Member() {
  const { isAuthenticated } = useAuth();
  const {
    isFacilitator,
    isSiteCord,
    isCountyCoordinator,
    isRegionalCountyCoordinator,
    isClusterLeader,
    isFinance,
    isDirector,
    isAdmin,
    isSuperAdmin,
    isData,
    isDataValidator,
    addresses,
  } = useRole();

  const { wardId, constituencyId, countyId, regionId } = isAuthenticated
    ? addresses[0]
    : {};
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  // const [addressData, setAddressData] = useState({});
  const [pages, setPages] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  const queryClient = useQueryClient();
  const { memberSummary } = useSelector((state) => ({
    memberSummary: state.reports.members,
  }));

  const getWardIdsValue = useCallback(() => {
    return getSearchColumns()?.find((t) => t.name === 'wardIds')?.value;
  }, []);

  // Call the function once to initialize wardIds value.
  const initialWardIds = getWardIdsValue();
  const dateFormat = 'DD-MM-YYYY';
  const [pagination, setPagination] = useState({
    pageSize: 10,
    pageNumber: 1,
    sortBy: 'id',
    ascendingSort: false,
    wardIds: initialWardIds,
    searchColumns: [
      ...(isFacilitator && (!allowedEmails.includes(localStorage.getItem('shofco_email')))
        ? [
          // {
          //   name: 'wardId',
          //   value: wardId,
          // },
        ]
        : []),
      ...(isCountyCoordinator
        ? [
          {
            name: 'countyIds',
            values: addresses?.map((c) => c.countyId),
          },
        ]
        : []),
      ...(isRegionalCountyCoordinator
        ? [
          {
            name: 'regionIds',
            values: addresses?.map((c) => c.regionId),
          },
        ]
        : []),
      ...(isClusterLeader
        ? [
          {
            name: 'constituencyIds',
            values: addresses?.map((c) => c.constituencyId),
          },
        ]
        : []),
      ...(isSiteCord
        ? [
          {
            name: 'constituencyIds',
            values: addresses?.map((c) => c.constituencyId),
          },
        ]
        : []),
      {
        name: 'endDate',
        value: moment().format(dateFormat),
      },
      {
        name: 'startDate',
        value: moment().startOf('year').format(dateFormat),
      },
    ],
  });

  const {
    mutate: mutateMemberCounts,
    isLoading: isLoadingMemberCounts,
    data,
  } = useMutation((payload) => getMemberStatusCountsApi(payload), {
    onSuccess: (res) => {
      dispatch(setMemberSummary(res?.data));
    },
    onSettled: () => {
      queryClient.invalidateQueries('get-all-counties');
    },
  });

  const {
    mutate: mutateMembers,
    isLoading: isLoadingMembers,
    data: membersData,
  } = useMutation((payload) => getAllAllMembersApi(payload), {
    onSuccess: (res) => {
      res.pages > 0 && setPages(res.pages);
    },
    onSettled: () => {
      setLoading(false);
      queryClient.invalidateQueries('get-all-counties');
    },
  });

  const membersCardData = [
    {
      title: 'Total Members',
      iconClass: 'bx bx-user',
      description: memberSummary?.length
        ? sumArray(memberSummary, 'totalMembers').toLocaleString()
        : 0,
      bgColor: "bg-primary"
    },
    {
      title: 'Dependants',
      iconClass: 'bx bx-group',
      description: memberSummary?.length
        ? sumArray(memberSummary, 'totalDependents').toLocaleString()
        : 0,
      bgColor: "bg-primary"
    },
    {
      title: 'Active Members',
      iconClass: 'bx bx-check-circle',
      description: memberSummary?.length
        ? sumArray(memberSummary, 'totalActiveMembers').toLocaleString()
        : 0,
      bgColor: "bg-success"
    },
    {
      title: 'Pending',
      iconClass: 'bx bx-check-circle',
      description: memberSummary?.length
        ? sumArray(memberSummary, 'totalPendingMembers').toLocaleString()
        : 0,
    },
    ...(isFacilitator ? [{
      title: 'Draft Members',
      iconClass: 'bx bx-check-circle',
      description: memberSummary?.length
        ? sumArray(memberSummary, 'totalDraftMembers').toLocaleString()
        : 0,
    },] : []),
    {
      title: 'Dormant Members',
      iconClass: 'bx bx-pause-circle',
      description: memberSummary?.length
        ? sumArray(memberSummary, 'totalDormantMembers').toLocaleString()
        : 0,
    },
    {
      title: 'Default Members',
      iconClass: 'bx bx-error-circle',
      description: memberSummary?.length
        ? sumArray(memberSummary, 'totalDefaultMembers').toLocaleString()
        : 0,
      bgColor: "bg-danger"
    },
  ];

  //meta title
  document.title = 'Members | SUN Welfare Administration System';

  const [modal1, setModal1] = useState(false);

  const [member, setMember] = useState(null);

  const toggleViewModal = () => setModal1(!modal1);

  useEffect(() => {
    console.log(pagination);
    mutateMembers({ ...pagination, pageNumber: pagination.pageNumber - 1 });
    const status = pagination.searchColumns.find((t) => t.name === 'status')
    mutateMemberCounts({
      fromDate: moment().startOf('month').format('DD-MM-YYYY'),
      toDate: moment().format('DD-MM-YYYY'),
      wardIds: pagination.searchColumns.find(s => s.name === 'wardIds')?.values,
      countyIds: pagination.searchColumns.find(s => s.name === 'countyIds')?.values,
      regionIds: pagination.searchColumns.find(s => s.name === 'regionIds')?.values,
      constituencyIds: pagination.searchColumns.find(s => s.name === 'constituencyIds')?.values,
      status: status?.value
    });
  }, [pagination]);

  //delete member
  const [deleteModal, setDeleteModal] = useState(false);

  const handleDeleteMember = () => {
    if (member && member.id) {
      dispatch(onDeleteMember(member.id));
      setDeleteModal(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: 'ID',
        accessor: 'memberId',
        hidden: true,
        filterable: true,
        Cell: (cellProps) => {
          return <MemberId {...cellProps} />;
        },
      },
      {
        Header: 'FULL NAME',
        accessor: 'fullName',
        filterable: true,
        Cell: (cellProps) => {
          return <PrimaryText {...cellProps} />;
        },
      },
      {
        Header: 'REGISTRATION DATE',
        accessor: 'joiningDate',
        filterable: true,
        Cell: (cellProps) => <span>{formatDate(cellProps.value, '')}</span>,
      },
      {
        Header: 'SHOFCO ID',
        accessor: 'shofcoId',
        filterable: true,
        Cell: (cellProps) => {
          return <MemberId {...cellProps} />;
        },
      },
      {
        Header: 'NATIONAL ID',
        accessor: 'identificationNumber',
        filterable: true,
        Cell: (cellProps) => {
          return <MemberId {...cellProps} />;
        },
      },
      {
        Header: 'MOBILE',
        accessor: 'phoneNumber1',
        filterable: true,
        Cell: (cellProps) => {
          return <MemberId {...cellProps} />;
        },
      },
      // {
      //   Header: 'EMAIL',
      //   accessor: 'email',
      //   filterable: true,
      //   Cell: (cellProps) => {
      //     return <MemberId {...cellProps} />;
      //   },
      // },
      {
        Header: 'COUNTY',
        accessor: (row) =>
          (row?.addresses || []).map((r) => r.countyName).filter(Boolean).join(', '),
        filterable: true,
        Cell: (cellProps) => <span className="text-body">{cellProps.value}</span>,
      },
      {
        Header: 'SUB COUNTY',
        accessor: (row) =>
          (row?.addresses || []).map((r) => r.constituencyName).filter(Boolean).join(', '),
        filterable: true,
        Cell: (cellProps) => <span className="text-body">{cellProps.value}</span>,
      },
      {
        Header: 'WARD NAME',
        accessor: (row) =>
          (row?.addresses || []).map((r) => r.wardName).filter(Boolean).join(', '),
        filterable: true,
        Cell: (cellProps) => <span className="text-body">{cellProps.value}</span>,
      },
      {
        Header: 'GROUP NAME',
        accessor: 'groupName',
        filterable: true,
        Cell: (cellProps) => {
          return <BillingName {...cellProps} />;
        },
      },
      {
        Header: 'MEMBER STATUS',
        accessor: 'status',
        Cell: (cellProps) => {
          return <PaymentStatus {...cellProps} />;
        },
      },
      {
        Header: 'POLICY STATUS',
        accessor: 'policyMatured',
        Cell: (cellProps) => {
          return <PolicyStatus {...cellProps} />;
        },
      },
    ],
    [],
  );

  const onChangePagination = ({ page, limit }, filters, sorter) => {
    const filterNames = Object.keys(filters || {}).filter(key => filters[key]);

    let baseSearchColumns = pagination.searchColumns.filter(
      col => !filterNames.includes(col.name)
    );

    let searchColumns = isEmpty(filters)
      ? pagination.searchColumns
      : [
        ...baseSearchColumns,
        ...(filters?.id
          ? [
            {
              name: 'id',
              value: filters?.id,
            },
          ]
          : []),
        ...(filters?.regionIds
          ? [
            {
              name: 'regionIds',
              values: filters?.regionIds,
            },
          ]
          : []),
        ...(filters?.countyIds
          ? [
            {
              name: 'countyIds',
              values: filters?.countyIds,
            },
          ]
          : []),
        ...(filters?.constituencyIds
          ? [
            {
              name: 'constituencyIds',
              values: filters?.constituencyIds,
            },
          ]
          : []),
        ...(filters?.wardIds
          ? [
            {
              name: 'wardIds',
              values: filters?.wardIds,
            },
          ]
          : []),
        ...(filters?.firstName
          ? [
            {
              name: 'firstName',
              value: filters?.firstName,
            },
          ]
          : []),
        ...(filters?.status
          ? [
            {
              name: 'status',
              value: filters?.status,
            },
          ]
          : []),
        ...(filters?.lastName
          ? [
            {
              name: 'lastName',
              value: filters?.lastName,
            },
          ]
          : []),
        ...(filters?.email
          ? [
            {
              name: 'email',
              value: filters?.email,
            },
          ]
          : []),
        ...(filters?.memberId
          ? [
            {
              name: 'memberId',
              value: filters?.memberId,
            },
          ]
          : []),
        ...(filters?.shofcoId
          ? [
            {
              name: 'shofcoId',
              value: filters?.shofcoId,
            },
          ]
          : []),
        ...(filters?.idNumber
          ? [
            {
              name: 'idNumber',
              value: filters?.idNumber,
            },
          ]
          : []),
        ...(filters?.mobileNumber1
          ? [
            {
              name: 'mobileNumber1',
              value: filters?.mobileNumber1,
            },
          ]
          : []),
      ];

    const newPagination = {
      ...pagination,
      pageNumber: page ?? pagination.pageNumber,
      pageSize: limit ?? pagination.pageSize,
      searchColumns,
    };

    setPagination(newPagination);
  };

  const onEdit = (row) => {
    navigate(`${location.pathname}/${row.memberId}/edit`);
  };

  const handleViewMember = (record) => {
    navigate(`${location.pathname}/${record.memberId}`);
  };

  const onSearch = (data) => {
    let searchColumns = [];

    searchColumns.push({
      name: 'endDate',
      value: moment().format('DD-MM-YYYY'),
    });

    searchColumns.push({
      name: 'startDate',
      value: moment().startOf('year').format('DD-MM-YYYY'),
    });

    let wardIds = [];
    let constituencyIds = [];
    let countyIds = [];
    let regionIds = [];

    if (isFacilitator) {
      wardIds = addresses.map(a => a.wardId);
    }

    if (isSiteCord) {
      wardIds = addresses.map(a => a.wardId);
      constituencyIds = addresses.map(a => a.constituencyId);
    }

    if (isCountyCoordinator) {
      countyIds = addresses.map(a => a.countyId);
    }

    if (isRegionalCountyCoordinator) {
      regionIds = addresses.map(a => a.regionId);
    }

    if (!isEmpty(data)) {
      if (data?.wardId?.value) {
        wardIds = [data.wardId.value];

        const matched = addresses.find(a => a.wardId === data.wardId.value);
        if (matched) {
          constituencyIds = [matched.constituencyId];
        }
      }

      if (data?.constituencyId?.value) {
        constituencyIds = [data.constituencyId.value];
      }

      if (data?.countyId?.value) {
        countyIds = [data.countyId.value];
      }

      if (data?.regionId?.value) {
        regionIds = [data.regionId.value];
      }

      data.startDate && searchColumns.push(data.startDate);
      data.endDate && searchColumns.push(data.endDate);
    }

    if (wardIds.length) {
      searchColumns.push({ name: 'wardIds', values: wardIds });
    }

    if (constituencyIds.length) {
      searchColumns.push({ name: 'constituencyIds', values: constituencyIds });
      searchColumns.push({ name: 'constituencyId', value: constituencyIds[0] });
    }

    if (countyIds.length) {
      searchColumns.push({ name: 'countyIds', values: countyIds });
    }

    if (regionIds.length) {
      searchColumns.push({ name: 'regionIds', values: regionIds });
    }

    const status = pagination.searchColumns.find((t) => t.name === 'status');
    if (status?.value) {
      searchColumns.push({ name: 'status', value: status.value });
    }

    setPagination({
      ...pagination,
      searchColumns,
      wardIds: wardIds.length ? wardIds : pagination.wardIds,
    });
  };

  return (
    <React.Fragment>
      <MembersModal isOpen={modal1} toggle={toggleViewModal} />
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteMember}
        onCloseClick={() => setDeleteModal(false)}
      />
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Home" breadcrumbItem="Members" />
          <StatCards items={membersCardData} loading={isLoadingMemberCounts} />
          <Card>
            <CardBody className="pb-0">
              <MainFilter
                inlineActions
                pagination={pagination}
                hasExport={isDataValidator || isSuperAdmin}
                pageSize={pages * pagination.pageSize}
                resource={'um/member'}
                onChangePagination={onChangePagination}
                onSearch={onSearch}
                showDateFilter
              />
            </CardBody>
          </Card>
          <Row>
            <Col xs="12">
              <Card>
                <CardBody>
                  <TableContainer
                    pageSizeAtBottom
                    columns={columns}
                    data={membersData?.list?.reverse() || []}
                    isGlobalFilter={true}
                    isAddOptions={true}
                    isPagination={true}
                    filterable={false}
                    hasCreate={!isData}
                    hasExport={isData || isSuperAdmin}
                    hasSearch
                    showEdit={
                      isAuthenticated && (isData || isSuperAdmin || isAdmin)
                    }
                    hasStatus
                    statuses={['Active', 'Pending', 'Dormant', 'Defaulted', 'Draft']}
                    showView
                    rowClickable
                    loading={isLoadingMembers}
                    pages={pages}
                    handleEdit={onEdit}
                    handleView={handleViewMember}
                    iscustomPageSizeOptions={true}
                    addText="Add New Member"
                    tableClass="align-middle table-nowrap table-check"
                    theadClass="table-light"
                    pagination="pagination pagination-rounded justify-content-end mb-2"
                    paginationData={pagination}
                    onChangePagination={onChangePagination}
                    searchByNames={[
                      {
                        value: 'id',
                        text: t('common.id'),
                      },
                      {
                        value: 'email',
                        text: t('common.email'),
                      },
                      {
                        value: 'idNumber',
                        text: t('common.idNumber'),
                      },
                      {
                        value: 'firstName',
                        text: t('users.firstName'),
                      },
                      {
                        value: 'lastName',
                        text: t('users.lastName'),
                      },
                      {
                        value: 'mobileNumber1',
                        text: t('common.mobileNumber'),
                      },
                    ]}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
      <ToastContainer />
    </React.Fragment>
  );
}
Member.propTypes = {
  preGlobalFilteredRows: PropTypes.any,
};

export default Member;
